import { generationAuditHeaders } from '@/utils/generationAudit';
import { extractBlobErrorMessage, extractBlobErrorPayload } from '@/utils/documentDownloadErrors';
/**
 * Axios API client with JWT authentication and automatic retry logic
 *
 * Features:
 * - JWT token injection from localStorage
 * - Automatic 3 retries with exponential backoff (1s, 2s, 4s)
 * - 401 handling (auto-logout)
 * - Request/response interceptors
 */

import axios from 'axios';
import { customGenerationFields } from '@/utils/customGeneration';
import { isImpersonatingSession } from '@/utils/impersonationClaim';
import { preparePassageForGeneration } from '@/utils/languageGuard';
import type {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import type {
  ApiError,
  ApiResponse,
  Workbook,
  WorkbookFilters,
  CreateWorkbookData,
  UpdateWorkbookData,
  ImportFromWorkbookPayload,
  ImportFromWorkbookResult,
  ImportFromWorkbookPlan,
  WorkbookMergePayload,
  WorkbookMergePreview,
  WorkbookMergePreviewOptions,
  Sheet,
  CreateSheetData,
  UpdateSheetData,
  UpdateRowData,
  UpdateRowResult,
  DeleteRowsByPassageResult,
  UserPreferences,
  QuestionPreset,
  GetUserPreferencesResponse,
  UpdateUserPreferencesResponse,
  GetExportDialogSelectionResponse,
  UpdateExportDialogSelectionResponse,
  ExportDialogSelectionsState,
  GenerationJob,
  GenerationJobPoll,
  GenerationJobResult,
  GenerationJobSummary,
  CreateJobRequest,
  CreateJobResponse,
  CancelJobResponse,
  ListJobsResponse,
  ActiveWorkbookGenerationJobResponse,
  GetCreditHistoryResponse,
  GetCreditHistoryParams,
  GetCreditHistoryExportParams,
  GetUsedFilesResponse,
  GetUsedFilesParams,
  FontSettings,
  MarginSettings,
  EditingOptions,
  RegenerateQuestionRequest,
  RegenerateQuestionResponse,
  RegenerateExplanationRequest,
  RegenerateExplanationResponse,
  GenerateAdditionalOptionsRequest,
  GenerateAdditionalOptionsResponse,
} from '@/types';
import { convertEditingOptionsToWireFormat } from '@/types/editingOptions';
import { finishApiDebug, startApiDebug, type DebugApiMeta } from '@/lib/debugApi';

// API configuration
// Use nullish coalescing to allow empty string (for relative URLs)
const API_BASE_URL = import.meta.env.VITE_API_URL ?? '';
const DEFAULT_TIMEOUT = 120000; // 120 seconds (2 minutes) for question generation (AI calls can take 40-60s)
const MAX_RETRIES = 3;
const USER_PREFERENCES_TIMEOUT_MS = 5000;
const USER_PREFERENCES_CACHE_TTL_MS = 1000;
const USER_PREFERENCES_UPDATE_DEBOUNCE_MS = 150;
const GENERATION_JOBS_LIST_CACHE_TTL_MS = 1000;

// Extend Axios request config to include retry tracking
interface WorkbooksApi {
  getAll: (filters?: WorkbookFilters) => Promise<Workbook[]>;
  getById: (id: number) => Promise<Workbook>;
  getRecent: (limit?: number) => Promise<Workbook[]>;
  create: (data: CreateWorkbookData) => Promise<Workbook>;
  update: (id: number, data: UpdateWorkbookData) => Promise<Workbook>;
  delete: (id: number) => Promise<void>;
  duplicate: (id: number) => Promise<Workbook>;
  previewMerge: (
    workbookIds: number[], options?: WorkbookMergePreviewOptions, signal?: AbortSignal,
  ) => Promise<WorkbookMergePreview>;
  merge: (payload: WorkbookMergePayload) => Promise<Workbook>;
  getTrash: () => Promise<Workbook[]>;
  restore: (id: number) => Promise<Workbook>;
  permanentDelete: (id: number) => Promise<void>;
  importExcelWithProcessing: (file: File, name?: string, replaceWorkbookId?: number) => Promise<{
    workbook: Workbook;
    import_summary: {
      total_sheets: number;
      total_rows: number;
      processed_rows: number;
      skipped_rows: number;
      sheets: Array<{ name: string; row_count: number }>;
    };
  }>;
  importFromWorkbook: (targetWorkbookId: number, payload: ImportFromWorkbookPayload) => Promise<ImportFromWorkbookResult>;
  previewImportFromWorkbook: (
    targetWorkbookId: number, payload: ImportFromWorkbookPayload, signal?: AbortSignal,
  ) => Promise<ImportFromWorkbookPlan>;
}

interface SheetsApi {
  getAll: (workbookId: number) => Promise<Sheet[]>;
  getById: (workbookId: number, sheetId: number) => Promise<Sheet>;
  create: (workbookId: number, data: CreateSheetData) => Promise<Sheet>;
  update: (workbookId: number, sheetId: number, data: UpdateSheetData) => Promise<Sheet>;
  reorder: (workbookId: number, orderedIds: number[]) => Promise<Sheet[]>;
  delete: (workbookId: number, sheetId: number) => Promise<void>;
  updateRow: (workbookId: number, sheetId: number, rowIndex: number, data: UpdateRowData) => Promise<UpdateRowResult>;
  sortByPassageId: (workbookId: number, sheetId: number) => Promise<Sheet>;
  bulkUpdateData: (workbookId: number, sheetId: number, data: UpdateSheetData['data']) => Promise<Sheet>;
  deleteRowsByPassage: (workbookId: number, sheetId: number, rowIndices: number[]) => Promise<DeleteRowsByPassageResult>;
}

interface RegenerationApi {
  regenerateQuestion: (params: RegenerateQuestionRequest) => Promise<RegenerateQuestionResponse>;
  regenerateExplanation: (params: RegenerateExplanationRequest) => Promise<RegenerateExplanationResponse>;
  generateAdditionalOptions: (params: GenerateAdditionalOptionsRequest) => Promise<GenerateAdditionalOptionsResponse>;
}

interface ExtendedAxiosInstance extends AxiosInstance {
  workbooks: WorkbooksApi;
  sheets: SheetsApi;
  regeneration: RegenerationApi;
}

interface RetryConfig extends InternalAxiosRequestConfig {
  _retry401?: boolean;
  _retryCount?: number;
  skipRetry?: boolean;
  skipAuthReset?: boolean;
}

// Public request-config shape carrying the skipRetry opt-out flag. Single source of
// truth — re-exported and reused by store.ts. Used to disable the transport-level
// auto-retry on expensive / non-idempotent POSTs.
export type RetryAwareAxiosConfig = AxiosRequestConfig & { skipRetry?: boolean };

// Shared opt-out config for non-idempotent / expensive requests, reused instead of
// repeating `{ skipRetry: true } as RetryAwareAxiosConfig` at each call site. Axios
// merges request config into a fresh object per call, so sharing this reference is safe.
export const NO_RETRY: RetryAwareAxiosConfig = { skipRetry: true };

// For endpoints whose 401 means "this link/form token expired" rather than "your session
// expired" (password reset link, social signup temp_token): no retry, and a 401 neither
// clears the stored session nor redirects to /login. The page explains the failure itself.
export const NO_RETRY_KEEP_SESSION: RetryAwareAxiosConfig & { skipAuthReset: boolean } = {
  skipRetry: true,
  skipAuthReset: true,
};

interface DebugRequestConfig extends InternalAxiosRequestConfig {
  __debugMeta?: DebugApiMeta | null;
  __debugLabel?: string;
}

type UserPreferencesResolver = {
  resolve: (value: UserPreferences) => void;
  reject: (reason?: unknown) => void;
};

type GenerationJobsListParams = {
  status?: 'queued' | 'processing' | 'completed' | 'cancelled' | 'failed' | 'orphaned';
  limit?: number;
  offset?: number;
};

let cachedUserPreferences:
  | {
      token: string | null;
      fetchedAt: number;
      value: UserPreferences;
    }
  | null = null;
let inFlightUserPreferencesGet: Promise<UserPreferences> | null = null;
let pendingUserPreferencesPatch: Partial<UserPreferences> | null = null;
// Latest desired values remain here until acknowledged, including failed saves.
let unsavedUserPreferencesPatch: Partial<UserPreferences> | null = null;
const pendingUserPreferencesResolvers: UserPreferencesResolver[] = [];
let userPreferencesUpdateTimer: ReturnType<typeof setTimeout> | null = null;
let inFlightUserPreferencesUpdate: Promise<UserPreferences> | null = null;
let userScopedStateVersion = 0;
const generationJobsListCache = new Map<
  string,
  { fetchedAt: number; value: ListJobsResponse }
>();
const generationJobsListInflight = new Map<string, Promise<ListJobsResponse>>();
let generationJobsListVersion = 0;

type UserScopeSnapshot = {
  token: string | null;
  version: number;
};

function getCurrentAuthToken(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  return window.localStorage.getItem('auth_token');
}

// Every screen that saves preferences goes through the two update() methods below; dropping
// the write there keeps an admin visit from changing the user's stored settings without each
// caller having to check. The gate itself reads the token claim (utils/impersonationClaim.ts).

function getGenerationJobsListCacheKey(
  params?: GenerationJobsListParams
): string {
  return JSON.stringify({
    token: getCurrentAuthToken(),
    ...params,
  });
}

function clearGenerationJobsListCache(): void {
  generationJobsListVersion += 1;
  generationJobsListCache.clear();
  generationJobsListInflight.clear();
}

function captureUserScopeSnapshot(): UserScopeSnapshot {
  return {
    token: getCurrentAuthToken(),
    version: userScopedStateVersion,
  };
}

function hasUserScopeChanged(snapshot: UserScopeSnapshot): boolean {
  return (
    snapshot.version !== userScopedStateVersion ||
    snapshot.token !== getCurrentAuthToken()
  );
}

function createUserScopeChangedError(): Error {
  const error = new Error('User-scoped request was cancelled after auth identity changed.');
  (error as Error & { code?: string }).code = 'USER_SCOPE_CHANGED';
  return error;
}

function assertUserScopeUnchanged(snapshot: UserScopeSnapshot): void {
  if (hasUserScopeChanged(snapshot)) {
    throw createUserScopeChangedError();
  }
}

function invalidateUserPreferencesCache(): void {
  cachedUserPreferences = null;
  inFlightUserPreferencesGet = null;
}

// Keep the earliest baseline while debounce/retry combines several edits. A whole
// replacement (preset/reset) clears it; later small edits still belong to that replacement.
function mergePreferencePatches(previous: Partial<UserPreferences> | null, next: Partial<UserPreferences>): Partial<UserPreferences> {
  const merged = { ...previous, ...next };
  if (next.oneclickFormat !== undefined) {
    if (next.oneclickFormatBase !== undefined && previous?.oneclickFormat !== undefined) {
      if (previous.oneclickFormatBase !== undefined) merged.oneclickFormatBase = previous.oneclickFormatBase;
      else delete merged.oneclickFormatBase;
    } else if (next.oneclickFormatBase === undefined) delete merged.oneclickFormatBase;
  }
  return merged;
}

function clearPendingUserPreferencesState(): void {
  if (userPreferencesUpdateTimer) {
    clearTimeout(userPreferencesUpdateTimer);
    userPreferencesUpdateTimer = null;
  }

  pendingUserPreferencesPatch = null;
  unsavedUserPreferencesPatch = null;

  const pendingResolvers = pendingUserPreferencesResolvers.splice(0);
  if (pendingResolvers.length === 0) {
    return;
  }

  const error = createUserScopeChangedError();
  for (const resolver of pendingResolvers) {
    resolver.reject(error);
  }
}

// Clear all user-scoped caches (call on auth identity change, e.g. impersonation)
export function clearAllUserScopedCaches(): void {
  userScopedStateVersion += 1;
  clearGenerationJobsListCache();
  invalidateUserPreferencesCache();
  clearPendingUserPreferencesState();
  inFlightUserPreferencesUpdate = null;
}

function setUserPreferencesCache(value: UserPreferences): UserPreferences {
  cachedUserPreferences = {
    token: getCurrentAuthToken(),
    fetchedAt: Date.now(),
    value,
  };
  return value;
}

function getCachedUserPreferences(): UserPreferences | null {
  if (!cachedUserPreferences) {
    return null;
  }

  const hasExpired =
    Date.now() - cachedUserPreferences.fetchedAt > USER_PREFERENCES_CACHE_TTL_MS;
  const tokenChanged = cachedUserPreferences.token !== getCurrentAuthToken();

  if (hasExpired || tokenChanged) {
    cachedUserPreferences = null;
    return null;
  }

  return cachedUserPreferences.value;
}

async function fetchUserPreferences(config?: RetryAwareAxiosConfig): Promise<UserPreferences> {
  const cached = getCachedUserPreferences();
  if (cached) {
    return cached;
  }

  if (inFlightUserPreferencesGet) {
    return inFlightUserPreferencesGet;
  }

  const requestSnapshot = captureUserScopeSnapshot();

  const response = config
    ? api.get<GetUserPreferencesResponse>('/api/user/preferences', config)
    : api.get<GetUserPreferencesResponse>('/api/user/preferences');
  const request = response
    .then((response) => {
      assertUserScopeUnchanged(requestSnapshot);
      return setUserPreferencesCache(
        extractData<UserPreferences>(response.data, ['preferences'])
      );
    })
    .finally(() => {
      if (inFlightUserPreferencesGet === request) {
        inFlightUserPreferencesGet = null;
      }
    });

  inFlightUserPreferencesGet = request;
  return request;
}

async function flushPendingUserPreferencesUpdate(): Promise<UserPreferences> {
  if (!pendingUserPreferencesPatch) {
    if (inFlightUserPreferencesUpdate) {
      return inFlightUserPreferencesUpdate;
    }
    return fetchUserPreferences();
  }

  const patch = pendingUserPreferencesPatch;
  const resolvers = pendingUserPreferencesResolvers.splice(0);
  const requestSnapshot = captureUserScopeSnapshot();

  pendingUserPreferencesPatch = null;

  const runUpdate = async () => {
    assertUserScopeUnchanged(requestSnapshot);

    if (inFlightUserPreferencesUpdate) {
      try {
        await inFlightUserPreferencesUpdate;
      } catch {
        // Preserve latest patch flush even if previous request failed.
      }
    }

    assertUserScopeUnchanged(requestSnapshot);

    const response = await api.put<UpdateUserPreferencesResponse>(
      '/api/user/preferences',
      patch,
      { ...NO_RETRY, timeout: USER_PREFERENCES_TIMEOUT_MS }
    );

    assertUserScopeUnchanged(requestSnapshot);

    // A newer queued change must survive an older request's acknowledgement.
    for (const key of Object.keys(patch) as (keyof UserPreferences)[]) {
      if (key === 'oneclickFormatBase') continue;
      if (unsavedUserPreferencesPatch && unsavedUserPreferencesPatch[key] === patch[key]) {
        delete unsavedUserPreferencesPatch[key];
        if (key === 'oneclickFormat') delete unsavedUserPreferencesPatch.oneclickFormatBase;
      }
    }
    if (unsavedUserPreferencesPatch && Object.keys(unsavedUserPreferencesPatch).length === 0) {
      unsavedUserPreferencesPatch = null;
    }

    return setUserPreferencesCache(
      extractData<UserPreferences>(response.data, ['preferences'])
    );
  };

  const request = runUpdate()
    .then((updatedPreferences) => {
      for (const resolver of resolvers) {
        resolver.resolve(updatedPreferences);
      }
      return updatedPreferences;
    })
    .catch((error) => {
      invalidateUserPreferencesCache();
      for (const resolver of resolvers) {
        resolver.reject(error);
      }
      throw error;
    })
    .finally(() => {
      if (inFlightUserPreferencesUpdate === request) {
        inFlightUserPreferencesUpdate = null;
      }
    });

  inFlightUserPreferencesUpdate = request;
  return request;
}

/**
 * Create Axios instance with base configuration
 */
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: DEFAULT_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
}) as ExtendedAxiosInstance;

/**
 * Request interceptor - Add JWT token to all requests
 */
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const debugMeta = startApiDebug({
      method: config.method,
      url:
        config.baseURL && config.url?.startsWith('/')
          ? `${config.baseURL}${config.url}`
          : config.url,
      params: config.params,
      data: config.data,
      label: (config as DebugRequestConfig).__debugLabel,
    });

    if (debugMeta) {
      (config as DebugRequestConfig).__debugMeta = debugMeta;
    }

    if (config.url?.startsWith('/api/support-chat/')
      || (['post', 'delete'].includes(config.method?.toLowerCase() ?? '')
        && /generation-jobs|oneclick\/(generate|ocr\/jobs)/.test(config.url ?? ''))) {
      for (const [name, value] of Object.entries(generationAuditHeaders())) {
        if (!config.headers[name]) config.headers[name] = value;
      }
    }
    const token = localStorage.getItem('auth_token');

    if (!config.headers['X-Client-Platform']) {
      config.headers['X-Client-Platform'] = 'web';
    }

    if (!config.headers['X-App-Build-Id']) {
      config.headers['X-App-Build-Id'] = __APP_BUILD_ID__;
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor - Handle errors and implement automatic retry
 */
api.interceptors.response.use(
  // Success response - pass through
  (response) => {
    const debugMeta = (response.config as DebugRequestConfig).__debugMeta;
    finishApiDebug(debugMeta, { status: response.status });
    return response;
  },

  // Error response - retry logic and error handling
  async (error: AxiosError) => {
    const debugMeta = (error.config as DebugRequestConfig | undefined)?.__debugMeta;
    finishApiDebug(debugMeta, error);

    const originalRequest = error.config as RetryConfig;
    const isCanceledRequest = axios.isCancel(error) || error.code === 'ERR_CANCELED';

    if (isCanceledRequest) {
      return Promise.reject(error);
    }

    // Handle 401 Unauthorized - token expired or invalid
    if (error.response?.status === 401 && originalRequest?.skipAuthReset) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry401) {
      originalRequest._retry401 = true;

      // If impersonating, restore the original admin credentials instead of logging out
      const originalToken = sessionStorage.getItem('admin_original_token');
      if (originalToken) {
        const originalUserRaw = sessionStorage.getItem('admin_original_user');
        localStorage.setItem('auth_token', originalToken);
        if (originalUserRaw) {
          localStorage.setItem('auth_user', originalUserRaw);
        }
        localStorage.removeItem('auth_validated_at');
        sessionStorage.removeItem('admin_original_token');
        sessionStorage.removeItem('admin_original_user');
        // Reload to reinitialize auth context with restored credentials
        window.location.href = '/admin/users';
        return Promise.reject(error);
      }

      // Clear all auth state (direct access to avoid circular import with auth.ts)
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      localStorage.removeItem('auth_validated_at');

      // Prevent redirect loop and skip redirect on public pages
      const publicPaths = ['/login', '/single-question', '/help'];
      const isPublicPage = publicPaths.some(p => window.location.pathname.startsWith(p));
      if (!isPublicPage) {
        const redirectTarget = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        window.location.href = `/login?redirect=${encodeURIComponent(redirectTarget)}`;
      }

      return Promise.reject(error);
    }

    // Handle 503 Maintenance mode - show maintenance message and stop
    if (error.response?.status === 503) {
      const responseData = error.response.data as Record<string, unknown>;
      if (responseData?.error === 'maintenance') {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        localStorage.removeItem('auth_validated_at');
        window.location.href = '/login?maintenance=true';
        return Promise.reject(error);
      }
    }

    // Handle 403 phone_not_verified - redirect to phone verification page
    if (error.response?.status === 403) {
      const responseData = error.response.data as Record<string, unknown>;
      if (responseData?.code === 'phone_not_verified') {
        // Clear auth state
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        localStorage.removeItem('auth_validated_at');

        // Redirect to phone verification/registration page
        window.location.href = '/register?phone_verification=required';
        return Promise.reject(error);
      }
    }

    // Automatic retry logic (3 times max)
    if (
      originalRequest &&
      !originalRequest.skipRetry &&
      (originalRequest._retryCount || 0) < MAX_RETRIES
    ) {
      // Network errors or 5xx server errors are retryable, but explicit rate limits are not.
      const status = error.response?.status;
      const isRetryableError =
        !error.response || // Network error
        (status !== undefined && status >= 500 && status < 600 && status !== 429); // Server error (not rate limited)

      if (isRetryableError) {
        originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;

        // Exponential backoff: 1s, 2s, 4s
        const delay = Math.pow(2, originalRequest._retryCount - 1) * 1000;

        console.log(
          `Retry attempt ${originalRequest._retryCount}/${MAX_RETRIES} after ${delay}ms`
        );

        await new Promise((resolve) => setTimeout(resolve, delay));

        return api(originalRequest);
      }
    }

    // Max retries exceeded or non-retryable error
    return Promise.reject(formatError(error));
  }
);

/**
 * Format Axios error into standardized ApiError
 */
function formatError(error: AxiosError): ApiError {
  if (error.response) {
    // Server responded with error status
    const data = error.response.data as ApiResponse;

    return {
      message: data.message || data.error || 'An error occurred',
      code: error.code,
      status: error.response.status,
      details: data,
      headers: error.response.headers as Record<string, unknown>,
    };
  } else if (error.request) {
    // Request made but no response received
    return {
      message: '서버에서 응답이 없습니다. 인터넷 연결을 확인해보시고, 문제가 지속되면 관리자에게 문의하세요.',
      code: error.code,
    };
  } else {
    // Error in request setup
    return {
      message: error.message || 'An unexpected error occurred',
      code: error.code,
    };
  }
}

function extractData<T>(payload: unknown, keys: string[]): T {
  if (payload && typeof payload === 'object') {
    for (const key of keys) {
      if (Object.prototype.hasOwnProperty.call(payload, key)) {
        return (payload as Record<string, unknown>)[key] as T;
      }
    }
  }

  return payload as T;
}

/**
 * Helper function to handle file uploads
 */
export function createFormDataRequest(file: File, additionalData?: Record<string, unknown>) {
  const formData = new FormData();
  formData.append('file', file);

  if (additionalData) {
    Object.entries(additionalData).forEach(([key, value]) => {
      formData.append(key, JSON.stringify(value));
    });
  }

  return formData;
}

/**
 * Helper function to download blob as file
 */
export function downloadBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  // Firefox can drop blob downloads if the object URL is revoked immediately.
  window.setTimeout(() => {
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, 1000);
}

function getFilenameFromContentDisposition(
  contentDisposition: string | undefined,
  fallback: string
): string {
  if (!contentDisposition) {
    return fallback;
  }

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;\s]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      // Fall through to the regular filename parser.
    }
  }

  const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
  if (filenameMatch?.[1]) {
    return filenameMatch[1].replace(/['"]/g, '');
  }
  return fallback;
}

/**
 * Type-safe API request wrapper
 */
export async function apiRequest<T = unknown>(
  config: AxiosRequestConfig
): Promise<T> {
  try {
    const response = await api(config);
    return response.data;
  } catch (error) {
    throw error as ApiError;
  }
}

/**
 * Options accepted by the workbook Word/HWPX export endpoint.
 */
export interface WordExportOptions {
  includeToc?: boolean;
  bookCoverId?: string;
  hwpxCoverId?: string;
  docxCoverId?: string;
  coverTextSchoolGrade?: string;
  coverTextExamType?: string;
  coverTextWeek?: string;
  includeThumbnails?: boolean;
  includeAnswerKey?: boolean;  // Legacy - kept for compatibility
  twoColumn?: boolean;
  marginSettings?: MarginSettings;
  exportMode?: 'by_type' | 'by_passage';
  passageOrder?: string[];
  selectedPassagesBySheet?: Record<string, string[]>;
  // New options for "해설지 분리" feature (separate downloads, no ZIP)
  separateAnswers?: boolean;  // Questions only (main doc)
  answersOnly?: boolean;      // Answers only (정답지)
  // Font settings for document export
  fontSettings?: FontSettings;
  // File format selection: 'word' for .docx, 'hwpx' for .hwpx (한글 문서)
  format?: 'word' | 'hwpx' | 'hwp';
  // Start each question type (sheet) on a new page
  newPagePerType?: boolean;
  // Insert page break after N passages
  pageBreakAfterPassages?: number;
  // Custom question ordering within sheets (byType mode)
  questionOrder?: Record<string, number[]>;
  // Excluded questions per sheet (byType mode)
  excludedQuestions?: Record<string, number[]>;
  // Identity of each excluded row, [trimmed passage_id, question, occurrence among
  // rows with that pair]; the server prefers it to the indices, which go stale
  // when generation inserts rows (my-flask-app/services/export_row_selection.py).
  excludedQuestionKeys?: Record<string, [string, string, number][]>;
  // Custom sheet ordering per passage (byPassage mode)
  sheetOrderByPassage?: Record<string, number[]>;
  // Passage source display options (byType mode only)
  showPassageSource?: boolean;
  passageSourcePosition?: 'question' | 'answer';
  // Editing options (export-only options for DOCX/HWPX generation)
  editingOptions?: EditingOptions;
  // Remove _Normal/_Hard suffix from sheet names
  removeDifficultyTag?: boolean;
  // Remove passage border (1x1 table) for specific question types
  removePassageBorder?: boolean;
}

/** Attempts of one workbook document export while the server answers busy (503 + retry). */
export const WORD_EXPORT_BUSY_MAX_ATTEMPTS = 6;
// Base waits between those attempts, about 30 s in total; each gets ±20% jitter so
// concurrent exports do not come back in lockstep.
const WORD_EXPORT_BUSY_DELAYS_MS = [2000, 3000, 5000, 8000, 12000];
// A server-sent Retry-After is honoured up to this bound.
const WORD_EXPORT_RETRY_AFTER_MAX_MS = 30000;
const WORD_EXPORT_BUSY_GIVE_UP_MESSAGE =
  '다른 문서를 만드는 요청이 많아 지금은 다운로드할 수 없습니다. 잠시 후 다시 시도해 주세요.';
const WORD_EXPORT_MEMORY_GIVE_UP_MESSAGE = '서버가 일시적으로 바쁩니다. 잠시 후 다시 시도해 주세요.';

/** Why the server asked to retry: another export holds the slot, or memory is high. */
export type WordExportBusyReason = 'capacity' | 'memory';

export interface WordExportRetryHooks {
  /** Called before each wait for a busy server, with the attempt that just failed. */
  onWait?: (attempt: number, maxAttempts: number, reason: WordExportBusyReason) => void;
}

// The server admits one document export per machine and answers the others with
// `503 { retry: true }` (machine_bounded_work); the memory guard answers the same
// way with `reason: "memory"`. Both are worth waiting out.
async function getWordExportBusyReason(error: unknown): Promise<WordExportBusyReason | null> {
  if ((error as { status?: unknown } | null)?.status !== 503) return null;
  const payload = await extractBlobErrorPayload((error as { details?: unknown }).details);
  if (payload?.retry !== true) return null;
  return payload.reason === 'memory' ? 'memory' : 'capacity';
}

/** Retry-After in whole seconds, as milliseconds; HTTP dates and junk give null. */
function getRetryAfterMs(error: unknown): number | null {
  const headers = (error as { headers?: unknown } | null)?.headers as
    | { get?: (name: string) => unknown; [key: string]: unknown }
    | undefined;
  if (!headers) return null;
  const raw = typeof headers.get === 'function' ? headers.get('retry-after') : headers['retry-after'];
  const text = String(raw ?? '').trim();
  if (!/^\d+$/.test(text)) return null;
  return Number(text) * 1000;
}

function createAbortError(): Error {
  const error = new Error('다운로드를 취소했습니다.');
  error.name = 'AbortError';
  return error;
}

// Resolves after `ms`, or rejects as soon as `signal` aborts.
function waitUnlessAborted(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(createAbortError());
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(createAbortError());
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Export API
 */
export const exportApi = {
  /**
   * Export workbook sheets to Word document (auto-triggers a browser download).
   *
   * Task 8.0: Enhanced to support answer key and two-column layout
   */
  async exportToWord(
    workbookId: number,
    sheetIds: number[],
    options?: WordExportOptions,
    retryHooks?: WordExportRetryHooks,
    signal?: AbortSignal
  ): Promise<void> {
    const { blob, filename } = await this.fetchWordExport(workbookId, sheetIds, options, retryHooks, signal);
    // A cancel that lands after the file arrived still means no download.
    if (signal?.aborted) throw createAbortError();
    downloadBlob(blob, filename);
  },

  /**
   * Fetch the word/hwpx export as a Blob without triggering a download.
   * Used when the UI needs to hand the file to the user via a manual-click modal
   * (e.g. Firefox/other browsers that block sequential auto-downloads).
   */
  async fetchWordExport(
    workbookId: number,
    sheetIds: number[],
    options?: WordExportOptions,
    retryHooks?: WordExportRetryHooks,
    signal?: AbortSignal
  ): Promise<{ blob: Blob; filename: string }> {
    const wireEditingOptions = options?.editingOptions
      ? convertEditingOptionsToWireFormat(options.editingOptions)
      : undefined;

    const requestBody = {
      workbook_id: workbookId,
      sheet_ids: sheetIds,
      book_cover_id: options?.bookCoverId ? Number(options.bookCoverId) : undefined,
      hwpx_cover_id: options?.hwpxCoverId || undefined,
      docx_cover_id: options?.docxCoverId || undefined,
      text_school_grade: options?.coverTextSchoolGrade,
      text_exam_type: options?.coverTextExamType,
      text_week: options?.coverTextWeek,
      include_thumbnails: options?.includeThumbnails || false,
      include_answer_key: options?.includeAnswerKey || false,
      two_column: options?.twoColumn || false,
      marginSettings: options?.marginSettings,
      export_mode: options?.exportMode,
      passage_order: options?.passageOrder,
      selected_passages_by_sheet: options?.selectedPassagesBySheet,
      // New parameters for "해설지 분리"
      separate_answers: options?.separateAnswers || false,
      answers_only: options?.answersOnly || false,
      // Font settings for document export
      font_settings: options?.fontSettings,
      // File format: 'word' (default) or 'hwpx'
      format: options?.format || 'word',
      // Start each question type (sheet) on a new page (default true)
      new_page_per_type: options?.newPagePerType !== false,
      // Insert page break after N passages
      page_break_after_passages: options?.pageBreakAfterPassages,
      // Custom question ordering within sheets (byType mode)
      question_order: options?.questionOrder,
      // Excluded questions per sheet (byType mode)
      excluded_questions: options?.excludedQuestions,
      excluded_question_keys: options?.excludedQuestionKeys,
      // Custom sheet ordering per passage (byPassage mode)
      sheet_order_by_passage: options?.sheetOrderByPassage,
      // Passage source display options (byType mode only)
      showPassageSource: options?.showPassageSource || false,
      passageSourcePosition: options?.passageSourcePosition,
      // Editing options (export-only options for DOCX/HWPX generation)
      editing_options: wireEditingOptions,
      // Remove _Normal/_Hard suffix from sheet names
      removeDifficultyTag: options?.removeDifficultyTag || false,
      includeToc: options?.includeToc,
      // Remove passage border for specific question types
      removePassageBorder: options?.removePassageBorder || false,
    };

    // NO_RETRY stays on: the transport retry would resend any 5xx. Only the busy
    // answer is retried, on the bounded schedule above.
    let response!: AxiosResponse<BlobPart>;
    for (let attempt = 1; ; attempt += 1) {
      if (signal?.aborted) throw createAbortError();
      try {
        response = await api.post<BlobPart>('/api/export/word', requestBody, {
          ...NO_RETRY,
          responseType: 'blob',
          timeout: 120000,
          signal,
        });
        break;
      } catch (error: unknown) {
        if (signal?.aborted) throw createAbortError();
        const busyReason = await getWordExportBusyReason(error);
        if (busyReason) {
          if (attempt >= WORD_EXPORT_BUSY_MAX_ATTEMPTS) {
            throw new Error(busyReason === 'memory' ? WORD_EXPORT_MEMORY_GIVE_UP_MESSAGE : WORD_EXPORT_BUSY_GIVE_UP_MESSAGE);
          }
          retryHooks?.onWait?.(attempt, WORD_EXPORT_BUSY_MAX_ATTEMPTS, busyReason);
          const retryAfterMs = getRetryAfterMs(error);
          // A server-given wait only stretches upward, so a retry never comes early.
          const delay = retryAfterMs !== null
            ? Math.min(Math.round(retryAfterMs * (1 + 0.2 * Math.random())), WORD_EXPORT_RETRY_AFTER_MAX_MS)
            : Math.round(WORD_EXPORT_BUSY_DELAYS_MS[attempt - 1] * (0.8 + 0.4 * Math.random()));
          await waitUnlessAborted(delay, signal);
          continue;
        }
        const details = (error as { details?: unknown })?.details;
        const message = await extractBlobErrorMessage(details);
        if (message) {
          // Keep the server's code (e.g. no_answer_content) for callers that act on it.
          const refused = new Error(message);
          const code = (await extractBlobErrorPayload(details))?.code;
          if (typeof code === 'string') (refused as Error & { code?: string }).code = code;
          throw refused;
        }
        throw error;
      }
    }

    // Extract filename from Content-Disposition header (supports UTF-8 encoded Korean filenames)
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'workbook.docx';

    if (contentDisposition) {
      // First try to extract UTF-8 encoded filename (RFC 5987 format: filename*=UTF-8''encoded_name)
      const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;\s]+)/i);
      if (utf8Match && utf8Match[1]) {
        try {
          filename = decodeURIComponent(utf8Match[1]);
        } catch {
          // Fallback to regular filename if decoding fails
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
          }
        }
      } else {
        // Fallback to regular filename format
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
    }

    // Determine MIME type based on Content-Type or filename
    const contentType = response.headers['content-type'];
    let mimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

    if (contentType) {
      if (contentType.includes('application/zip')) {
        mimeType = 'application/zip';
        // Update filename extension if needed
        if (!filename.endsWith('.zip')) {
          filename = filename.replace(/\.[^.]+$/, '.zip');
        }
      } else if (contentType.includes('application/vnd.hancom.hwpx')) {
        mimeType = 'application/vnd.hancom.hwpx';
        if (!filename.endsWith('.hwpx')) {
          filename = filename.includes('.')
            ? filename.replace(/\.[^.]+$/, '.hwpx')
            : `${filename}.hwpx`;
        }
      } else if (contentType.includes('application/x-hwp')) {
        mimeType = 'application/x-hwp';
        filename = filename.replace(/\.[^.]+$/, '.hwp');
      } else if (contentType.includes('application/vnd.openxmlformats')) {
        mimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      }
    }

    // Fallback: align filename/mime with requested format if headers are ambiguous
    if ((options?.format === 'hwpx' || options?.format === 'hwp') && !filename.endsWith(`.${options.format}`)) {
      filename = filename.includes('.')
        ? filename.replace(/\.[^.]+$/, `.${options.format}`)
        : `${filename}.${options.format}`;
      mimeType = options.format === 'hwp' ? 'application/x-hwp' : 'application/vnd.hancom.hwpx';
    }

    const blob = new Blob([response.data], { type: mimeType });
    return { blob, filename };
  },
};

/**
 * LMS Export API (SPEC-LMS-EXPORT-001, SPEC-AUTOQM-LINK-001)
 */
export interface LmsExportResponse {
  success: boolean;
  exam_id?: number;
  exam_url?: string;
  homework_id?: number;
  title: string;
  question_count: number;
  sheet_count?: number;
  mode?: 'append';
}


export interface LmsWorkbookExportResponse {
  success: boolean;
  homework_id: number;
  title: string;
  sheet_count: number;
  question_count: number;
  mode?: 'append';
}

export interface LmsHomeworkItem {
  id: number;
  title: string;
  source?: string;
  autoqm_workbook_id?: string | null;
  total_sheets: number;
  status?: string;
  updated_at?: string | null;
}
/**
 * Status of the user's LMS account link (REQ-004).
 * linked:true means an active LmsLink record exists.
 * linked:false means the user must link first; there is no shared env fallback.
 */
export interface LmsLinkStatus {
  linked: boolean;
  academy_id?: number;
  academy_name?: string;
  academy_url?: string;
  linked_at?: string;
  needs_selection?: boolean;
  academies?: Array<{ id: number; name: string }>;
}

/**
 * Result from POST /api/lms/link (REQ-005).
 * On success: linked:true with academy info.
 * On multi-academy: needs_selection:true with academies list.
 */
export interface LmsLinkResult {
  linked?: boolean;
  academy_id?: number;
  academy_name?: string;
  academy_url?: string;
  needs_selection?: boolean;
  academies?: Array<{ id: number; name: string }>;
  error?: string;
}

export interface LmsSelectedGroup {
  name: string;
  passage?: boolean;
  sources: Array<{ sheet_id: number; row_indices: number[]; sheet_name: string; expected_rows: Record<string, string>[] }>;
}

export interface LmsSelectionPreview {
  sheet_count: number;
  question_count: number;
  skipped: Array<{ sheet_name: string; row_number: number; reason: string }>;
  preview_token: string;
}

export interface LmsClassroomItem {
  id: number;
  name: string;
}

export interface LmsLessonItem {
  id: number;
  week: number;
  title: string;
  lesson_date: string | null;
  is_published: boolean;
  homework_ids: number[];
}

export interface LmsLinkLessonsResult {
  homework_id: number;
  linked_lesson_ids: number[];
  already_linked_lesson_ids: number[];
}

// Export and link POSTs make the backend create data in Classro (homework, exam,
// sheets, an API key). A 502/timeout can arrive after Classro already committed, so
// an automatic resend would duplicate it — these calls opt out of the retry interceptor.
export const lmsApi = {
  async previewWorkbookSelection(workbookId: number, selectedGroups: LmsSelectedGroup[]): Promise<LmsSelectionPreview> {
    const response = await api.post<LmsSelectionPreview>('/api/lms/export-workbook', {
      workbook_id: workbookId,
      sheet_ids: [...new Set(selectedGroups.flatMap(group => group.sources.map(source => source.sheet_id)))],
      selected_groups: selectedGroups,
      dry_run: true,
    });
    return response.data;
  },
  async getWorkbookPassages(workbookId: number) {
    const response = await api.get<{ passages: import('../components/dialogs/LmsPassageSelector').LmsPassage[] }>(`/api/lms/workbooks/${workbookId}/passages`);
    return response.data.passages;
  },
  async exportToLms(
    workbookId: number,
    sheetId: number,
    titleOverride?: string | null,
    targetHomeworkId?: number | null,
  ): Promise<LmsExportResponse> {
    const response = await api.post<LmsExportResponse>('/api/lms/export', {
      workbook_id: workbookId,
      sheet_id: sheetId,
      title_override: titleOverride || null,
      target_homework_id: targetHomeworkId || null,
    }, NO_RETRY);
    return response.data;
  },


  async exportWorkbookToLms(
    workbookId: number,
    titleOverride?: string | null,
    sheetIds?: number[],
    targetHomeworkId?: number | null,
    selection?: { sort_mode: 'byPassage'; passages: import('../components/dialogs/LmsPassageSelector').PassageSelection[] } | { selected_groups: LmsSelectedGroup[]; preview_token: string },
  ): Promise<LmsWorkbookExportResponse> {
    const response = await api.post<LmsWorkbookExportResponse>('/api/lms/export-workbook', {
      ...selection,
      workbook_id: workbookId,
      title_override: titleOverride || null,
      sheet_ids: sheetIds || null,
      target_homework_id: targetHomeworkId || null,
    }, NO_RETRY);
    return response.data;
  },

  async getLmsHomeworks(): Promise<LmsHomeworkItem[]> {
    const response = await api.get<{ homeworks: LmsHomeworkItem[] }>('/api/lms/homeworks');
    return response.data.homeworks;
  },

  async getLmsClassrooms(): Promise<LmsClassroomItem[]> {
    const response = await api.get<{ classrooms: LmsClassroomItem[] }>('/api/lms/classrooms');
    return response.data.classrooms;
  },

  async getLmsClassroomLessons(classroomId: number): Promise<LmsLessonItem[]> {
    const response = await api.get<{ lessons: LmsLessonItem[] }>(`/api/lms/classrooms/${classroomId}/lessons`);
    return response.data.lessons;
  },

  async linkLmsHomeworkLessons(
    homeworkId: number,
    classroomId: number,
    lessonIds: number[],
  ): Promise<LmsLinkLessonsResult> {
    const response = await api.post<LmsLinkLessonsResult>(`/api/lms/homeworks/${homeworkId}/lessons`, {
      classroom_id: classroomId,
      lesson_ids: lessonIds,
    }, NO_RETRY);
    return response.data;
  },

  async getLmsLink(): Promise<LmsLinkStatus> {
    const response = await api.get<LmsLinkStatus>('/api/lms/link');
    return response.data;
  },

  async linkLmsAccount(
    email: string,
    password: string,
    academyId?: number,
  ): Promise<LmsLinkResult> {
    const response = await api.post<LmsLinkResult>('/api/lms/link', {
      email,
      password,
      academy_id: academyId ?? null,
    }, NO_RETRY);
    return response.data;
  },

  async unlinkLmsAccount(): Promise<void> {
    await api.delete('/api/lms/link');
  },
};

/**
 * Check which passages have EBS fuzzy matches.
 * Returns a set of passage IDs that matched.
 */
const EBS_BATCH_CHUNK_SIZE = 50;
const EBS_BATCH_MAX_CONCURRENCY = 3;

export async function checkEbsBatch(
  passages: Array<{ id: string; text: string }>
): Promise<Set<string>> {
  const uniquePassages: Array<{ id: string; text: string }> = [];
  const seenPassageIds = new Set<string>();

  for (const passage of passages) {
    if (!passage.id || !passage.text?.trim() || seenPassageIds.has(passage.id)) {
      continue;
    }
    seenPassageIds.add(passage.id);
    uniquePassages.push(passage);
  }

  if (uniquePassages.length === 0) {
    return new Set();
  }

  const chunks: Array<Array<{ id: string; text: string }>> = [];
  for (let start = 0; start < uniquePassages.length; start += EBS_BATCH_CHUNK_SIZE) {
    chunks.push(uniquePassages.slice(start, start + EBS_BATCH_CHUNK_SIZE));
  }

  const matchedPassageIds = new Set<string>();

  // Keep concurrency bounded: this avoids serial UI delay for large selections
  // without flooding the CPU-heavy fuzzy-match endpoint. Each optional chunk
  // fails independently, and successful matches are retained.
  for (let start = 0; start < chunks.length; start += EBS_BATCH_MAX_CONCURRENCY) {
    const wave = chunks.slice(start, start + EBS_BATCH_MAX_CONCURRENCY);
    const waveMatches = await Promise.all(
      wave.map(async (chunk) => {
        try {
          const response = await api.post(
            '/check_ebs_batch',
            { passages: chunk },
            NO_RETRY,
          );
          const matches = response.data?.matches;
          if (!matches || typeof matches !== 'object' || Array.isArray(matches)) {
            return [] as string[];
          }
          const chunkIds = new Set(chunk.map((passage) => passage.id));
          return Object.keys(matches).filter((passageId) => chunkIds.has(passageId));
        } catch {
          // EBS matching is optional. Skip only the failed chunk.
          return [] as string[];
        }
      }),
    );

    for (const matchedIds of waveMatches) {
      for (const passageId of matchedIds) {
        matchedPassageIds.add(passageId);
      }
    }
  }

  return matchedPassageIds;
}

export default api;

/**
 * Workbooks API
 *
 * Note: Backend now returns 'title' field directly (mapped from database 'name' column).
 * No transformation needed since backend handles the field mapping in to_dict().
 */
api.workbooks = {
  async getAll(filters?: WorkbookFilters) {
    const response = await api.get('/api/workbooks', { params: filters });
    const workbooks = extractData<Workbook[]>(response.data, ['workbooks', 'data']);
    return workbooks;
  },

  async getById(id: number) {
    const response = await api.get(`/api/workbooks/${id}`);
    const workbook = extractData<Workbook>(response.data, ['workbook', 'data']);
    return workbook;
  },

  async getRecent(limit: number = 5) {
    const response = await api.get('/api/workbooks/recent', { params: { limit } });
    const workbooks = extractData<Workbook[]>(response.data, ['workbooks', 'data']);
    return workbooks;
  },

  async create(data: CreateWorkbookData) {
    // Backend accepts both 'title' and 'name' (legacy support)
    // Send as 'title' to match frontend convention
    const payload = {
      title: data.title,
      description: data.description,
      settings: data.settings,
    };
    const response = await api.post('/api/workbooks', payload);
    const workbook = extractData<Workbook>(response.data, ['workbook', 'data']);
    return workbook;
  },

  async update(id: number, data: UpdateWorkbookData) {
    // Backend accepts both 'title' and 'name' (legacy support)
    // Send as 'title' to match frontend convention
    const payload: Record<string, unknown> = {};
    if (data.title !== undefined) payload.title = data.title;
    if (data.description !== undefined) payload.description = data.description;
    if (data.settings !== undefined) payload.settings = data.settings;

    const response = await api.put(`/api/workbooks/${id}`, payload);
    const workbook = extractData<Workbook>(response.data, ['workbook', 'data']);
    return workbook;
  },

  async delete(id: number) {
    await api.delete(`/api/workbooks/${id}`);
  },

  async duplicate(id: number) {
    const response = await api.post(`/api/workbooks/${id}/duplicate`);
    const workbook = extractData<Workbook>(response.data, ['workbook', 'data']);
    return workbook;
  },

  // Summary of the workbooks selected for a merge, in the given order; the
  // options make its size check match the merge the dialog would send.
  async previewMerge(workbookIds: number[], options: WorkbookMergePreviewOptions = {}, signal?: AbortSignal) {
    const response = await api.post<WorkbookMergePreview>(
      '/api/workbooks/merge/preview',
      { workbook_ids: workbookIds, ...options },
      { signal },
    );
    return response.data;
  },

  // Merges workbooks into one new workbook; the response carries counts, not sheets.
  // Not retried: a merge re-sent after a lost response creates a second workbook.
  async merge(payload: WorkbookMergePayload) {
    const response = await api.post('/api/workbooks/merge', payload, NO_RETRY);
    return extractData<Workbook>(response.data, ['workbook', 'data']);
  },

  async getTrash() {
    const response = await api.get('/api/workbooks/trash');
    return extractData<Workbook[]>(response.data, ['workbooks', 'data']);
  },

  async restore(id: number) {
    const response = await api.post(`/api/workbooks/${id}/restore`);
    return extractData<Workbook>(response.data, ['workbook', 'data']);
  },

  async permanentDelete(id: number) {
    await api.delete(`/api/workbooks/${id}/permanent`);
  },

  /**
   * Import Excel file with post-processing for C/D/E columns.
   * Workbook must contain "대기열"; no data in F+ columns and A/B pairing required.
   */
  async importExcelWithProcessing(file: File, name?: string, replaceWorkbookId?: number): Promise<{
    workbook: Workbook;
    import_summary: {
      total_sheets: number;
      total_rows: number;
      processed_rows: number;
      skipped_rows: number;
      sheets: Array<{ name: string; row_count: number }>;
    };
  }> {
    const formData = new FormData();
    formData.append('file', file);
    if (name) {
      formData.append('name', name);
    }

    if (replaceWorkbookId !== undefined) {
      formData.append('replace_workbook_id', String(replaceWorkbookId));
    }

    const response = await api.post('/api/workbooks/import-with-processing', formData, {
      ...NO_RETRY,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 120000, // 2 minutes for large files
    });

    return response.data;
  },

  /**
   * Copy selected passages/types from another workbook into this one.
   * Non-idempotent (rows are appended each call), so retries are disabled.
   */
  async importFromWorkbook(targetWorkbookId: number, payload: ImportFromWorkbookPayload): Promise<ImportFromWorkbookResult> {
    const response = await api.post(
      `/api/workbooks/${targetWorkbookId}/import-from-workbook`,
      payload,
      NO_RETRY,
    );
    return response.data;
  },

  // Same request as importFromWorkbook, but the server only reports what it would do.
  // The route itself is the dry run: a server without it answers 404 instead of
  // importing, which a dry_run flag in the body could not guarantee.
  async previewImportFromWorkbook(
    targetWorkbookId: number, payload: ImportFromWorkbookPayload, signal?: AbortSignal,
  ): Promise<ImportFromWorkbookPlan> {
    const response = await api.post(
      `/api/workbooks/${targetWorkbookId}/import-from-workbook/preview`,
      payload,
      { signal },
    );
    return response.data;
  },
};

/**
 * Sheets API
 */
api.sheets = {
  async getAll(workbookId: number) {
    const response = await api.get(`/api/workbooks/${workbookId}/sheets`);
    return extractData<Sheet[]>(response.data, ['sheets', 'data']);
  },

  async getById(workbookId: number, sheetId: number) {
    const response = await api.get(`/api/workbooks/${workbookId}/sheets/${sheetId}`);
    return extractData<Sheet>(response.data, ['sheet', 'data']);
  },

  async create(workbookId: number, data: CreateSheetData) {
    const response = await api.post(`/api/workbooks/${workbookId}/sheets`, data);
    return extractData<Sheet>(response.data, ['sheet', 'data']);
  },

  async update(workbookId: number, sheetId: number, data: UpdateSheetData) {
    const response = await api.put(`/api/workbooks/${workbookId}/sheets/${sheetId}`, data);
    return extractData<Sheet>(response.data, ['sheet', 'data']);
  },

  /** Persist sheet ordering in one request; index in orderedIds becomes sheet_order. */
  async reorder(workbookId: number, orderedIds: number[]) {
    const response = await api.patch(`/api/workbooks/${workbookId}/sheets/reorder`, {
      sheet_ids: orderedIds,
    });
    return extractData<Sheet[]>(response.data, ['sheets', 'data']);
  },

  async delete(workbookId: number, sheetId: number) {
    await api.delete(`/api/workbooks/${workbookId}/sheets/${sheetId}`);
  },

  async updateRow(workbookId: number, sheetId: number, rowIndex: number, data: UpdateRowData) {
    const response = await api.patch(
      `/api/workbooks/${workbookId}/sheets/${sheetId}/rows/${rowIndex}`,
      data
    );
    return extractData<UpdateRowResult>(response.data, ['row_update', 'data']);
  },

  async sortByPassageId(workbookId: number, sheetId: number) {
    const response = await api.post(`/api/workbooks/${workbookId}/sheets/${sheetId}/sort`);
    return extractData<Sheet>(response.data, ['sheet', 'data']);
  },

  async bulkUpdateData(workbookId: number, sheetId: number, data: UpdateSheetData['data']) {
    const response = await api.patch(`/api/workbooks/${workbookId}/sheets/${sheetId}/data`, {
      data: data
    });
    return extractData<Sheet>(response.data, ['sheet', 'data']);
  },

  /**
   * Delete the selected rows' passages from every sheet of the workbook
   * ("모든 유형에서 삭제"). Index-based, so not idempotent: retries are disabled.
   */
  async deleteRowsByPassage(workbookId: number, sheetId: number, rowIndices: number[]): Promise<DeleteRowsByPassageResult> {
    const response = await api.post(
      `/api/workbooks/${workbookId}/sheets/delete-by-passage`,
      { sheet_id: sheetId, row_indices: rowIndices },
      NO_RETRY,
    );
    return response.data;
  },
};

/**
 * Regeneration API (desktop parity for review dialog)
 */
api.regeneration = {
  async regenerateQuestion(params: RegenerateQuestionRequest) {
    const cleaned = {
      ...params,
      ...customGenerationFields(params.sheet_type, params.option),
      passage: params.passage
        .replace(/%5B/g, '[')
        .replace(/%5D/g, ']')
        .replace(/%25/g, '%'),
    };
    // skipRetry: these endpoints run an expensive, non-deterministic AI generation.
    // A transport-level auto-retry after a slow/failed response leg would re-run the
    // whole generation; let the dialog surface a manual retry instead.
    const response = await api.post<RegenerateQuestionResponse>('/process_passage', cleaned, NO_RETRY);
    return response.data;
  },

  async regenerateExplanation(params: RegenerateExplanationRequest) {
    const response = await api.post<RegenerateExplanationResponse>('/process_재해설', params, NO_RETRY);
    return response.data;
  },

  async generateAdditionalOptions(params: GenerateAdditionalOptionsRequest) {
    const response = await api.post<GenerateAdditionalOptionsResponse>('/process_추가선지', params, NO_RETRY);
    return response.data;
  },
};

// User Preferences API
export const userPreferencesApi = {
  async get() {
    const snapshot = captureUserScopeSnapshot();
    const preferences = await fetchUserPreferences();
    assertUserScopeUnchanged(snapshot);
    return { ...preferences, ...unsavedUserPreferencesPatch };
  },

  async getFresh() {
    const snapshot = captureUserScopeSnapshot();
    try {
      // Drain both the debounce queue and in-flight writes before reading.
      // Providers hand changes to this queue before navigation can unmount them.
      while (pendingUserPreferencesPatch || inFlightUserPreferencesUpdate || unsavedUserPreferencesPatch) {
        if (!pendingUserPreferencesPatch && !inFlightUserPreferencesUpdate) {
          pendingUserPreferencesPatch = { ...unsavedUserPreferencesPatch };
        }
        if (userPreferencesUpdateTimer) {
          clearTimeout(userPreferencesUpdateTimer);
          userPreferencesUpdateTimer = null;
        }
        await flushPendingUserPreferencesUpdate();
        assertUserScopeUnchanged(snapshot);
      }
    } catch (cause) {
      assertUserScopeUnchanged(snapshot);
      throw Object.assign(new Error('문서 양식 설정을 저장하지 못했습니다. 다시 시도해 주세요.'), {
        code: 'USER_PREFERENCES_SYNC_FAILED', cause,
      });
    }
    assertUserScopeUnchanged(snapshot);
    invalidateUserPreferencesCache();
    return fetchUserPreferences({ ...NO_RETRY, timeout: USER_PREFERENCES_TIMEOUT_MS });
  },

  async update(preferences: Partial<UserPreferences>) {
    if (isImpersonatingSession()) {
      // Nothing is queued either: the patch must not reach the server after the admin's
      // own session is restored.
      return { ...preferences } as UserPreferences;
    }
    invalidateUserPreferencesCache();

    unsavedUserPreferencesPatch = mergePreferencePatches(unsavedUserPreferencesPatch, preferences);
    pendingUserPreferencesPatch = mergePreferencePatches(pendingUserPreferencesPatch, preferences);
    if (preferences.oneclickFormat !== undefined) {
      if (unsavedUserPreferencesPatch.oneclickFormatBase !== undefined) {
        pendingUserPreferencesPatch.oneclickFormatBase = unsavedUserPreferencesPatch.oneclickFormatBase;
      } else delete pendingUserPreferencesPatch.oneclickFormatBase;
    }

    return new Promise<UserPreferences>((resolve, reject) => {
      pendingUserPreferencesResolvers.push({ resolve, reject });

      if (userPreferencesUpdateTimer) {
        clearTimeout(userPreferencesUpdateTimer);
      }

      userPreferencesUpdateTimer = setTimeout(() => {
        userPreferencesUpdateTimer = null;
        // update() callers receive the rejection via their queued promises.
        void flushPendingUserPreferencesUpdate().catch(() => {});
      }, USER_PREFERENCES_UPDATE_DEBOUNCE_MS);
    });
  },

  async mutateOneclickPreset(operation: Record<string, unknown>): Promise<UserPreferences> {
    if (isImpersonatingSession()) throw new Error('대리 로그인 중에는 프리셋을 변경할 수 없습니다.');
    const snapshot = captureUserScopeSnapshot();
    // Mutations are atomic and idempotent at the server. They never enter the
    // autosave retry queue, which otherwise silently replays a failed list replacement.
    const response = await api.post<GetUserPreferencesResponse>(
      '/api/user/preferences/oneclick-presets', operation,
      { ...NO_RETRY, timeout: USER_PREFERENCES_TIMEOUT_MS },
    );
    assertUserScopeUnchanged(snapshot);
    invalidateUserPreferencesCache();
    return extractData<UserPreferences>(response.data, ['preferences']);
  },

  // Seed the preferences cache from editor-init response data.
  // This prevents Font/Margin/DocEditing providers from making a separate
  // GET /api/user/preferences call when they hydrate from server.
  seedFromEditorInit(editorPreferences: {
    font: FontSettings | null;
    margin: MarginSettings | null;
    doc_editing: EditingOptions | null;
  }): void {
    const synthetic: UserPreferences = {
      fontSettings: editorPreferences.font ?? undefined,
      marginSettings: editorPreferences.margin ?? undefined,
      editingOptions: editorPreferences.doc_editing ?? undefined,
    };
    setUserPreferencesCache(synthetic);
  },

  // Preset-specific helpers
  async savePreset(preset: Omit<QuestionPreset, 'id' | 'createdAt'>) {
    const currentPrefs = await this.get();
    const newPreset: QuestionPreset = {
      ...preset,
      id: `preset_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
      createdAt: new Date().toISOString(),
    };

    const updatedPresets = [...(currentPrefs.questionPresets || []), newPreset];
    await this.update({ questionPresets: updatedPresets });
    return newPreset;
  },

  async deletePreset(presetId: string) {
    const currentPrefs = await this.get();
    const updatedPresets = (currentPrefs.questionPresets || []).filter(
      (p) => p.id !== presetId
    );
    return await this.update({ questionPresets: updatedPresets });
  },

  async getPresetsForType(questionType: string) {
    const prefs = await this.get();
    return (prefs.questionPresets || []).filter((p) => p.questionType === questionType);
  },
};

export const exportDialogSelectionsApi = {
  async get(workbookId: number): Promise<ExportDialogSelectionsState | null> {
    const response = await api.get<GetExportDialogSelectionResponse>(
      `/api/user/preferences/export-dialog-selections/${workbookId}`
    );
    return response.data.selection ?? null;
  },

  async update(
    workbookId: number,
    selection: ExportDialogSelectionsState | null
  ): Promise<ExportDialogSelectionsState | null> {
    if (isImpersonatingSession()) {
      return selection;
    }
    const response = await api.put<UpdateExportDialogSelectionResponse>(
      `/api/user/preferences/export-dialog-selections/${workbookId}`,
      { selection }
    );
    return response.data.selection ?? null;
  },
};

/**
 * Generation Jobs API (Background Question Generation)
 *
 * Task 4.1: Backend job queue for browser-independent question generation
 */
export const generationJobsApi = {
  /**
   * Create a new generation job
   *
   * Starts background question generation that continues even if browser is closed.
   */
  async createJob(request: CreateJobRequest): Promise<CreateJobResponse> {
    const preparedRequest = {
      ...request,
      passages: request.passages.map((passage, index) => ({
        ...passage,
        passage: preparePassageForGeneration(
          passage.passage,
          request.workbook_id == null ? '지문' : `${index + 1}번 지문 (${passage.passage_id})`,
        ),
      })),
    };
    // skipRetry: job creation is non-idempotent (starts background generation +
    // credit deduction). A transport-level retry after a slow/failed response leg
    // could spawn a duplicate job; the backend's active-job/idempotency guards are
    // the primary defense, this is defense-in-depth matching the payment endpoints.
    const response = await api.post<CreateJobResponse>('/api/generation-jobs', preparedRequest, NO_RETRY);
    clearGenerationJobsListCache();
    // Backend returns data directly (not wrapped)
    return response.data;
  },

  /**
   * Get current status of a generation job
   *
   * Used for polling to track progress.
   */
  async getJob<
    TResult extends GenerationJobResult = GenerationJobResult,
  >(jobId: string): Promise<GenerationJobPoll<TResult>> {
    const response = await api.get<GenerationJobPoll<TResult>>(`/api/generation-jobs/${jobId}`);
    // Backend returns job data directly (not wrapped)
    return response.data;
  },

  /** Fetch the large request payload only for retry/recovery workflows. */
  async getJobDetails<
    TResult extends GenerationJobResult = GenerationJobResult,
  >(jobId: string): Promise<GenerationJob<TResult>> {
    const response = await api.get<GenerationJob<TResult>>(
      `/api/generation-jobs/${jobId}?detail=retry`
    );
    return response.data;
  },

  /**
   * Cancel a running generation job
   *
   * Works even after page refresh or from different device.
   */
  async cancelJob(jobId: string, action = 'api_cancel'): Promise<CancelJobResponse> {
    const response = await api.post<CancelJobResponse>(`/api/generation-jobs/${jobId}/cancel`, undefined, { headers: generationAuditHeaders(action) });
    clearGenerationJobsListCache();
    // Backend returns data directly (not wrapped)
    return response.data;
  },

  /**
   * List user's generation jobs
   *
   * @param status - Filter by status (optional)
   * @param limit - Max results (default 20, max 100)
   * @param offset - Pagination offset (default 0)
   */
  async listJobs(params?: GenerationJobsListParams): Promise<ListJobsResponse> {
    const cacheKey = getGenerationJobsListCacheKey(params);
    const cached = generationJobsListCache.get(cacheKey);

    if (
      cached &&
      Date.now() - cached.fetchedAt < GENERATION_JOBS_LIST_CACHE_TTL_MS
    ) {
      return cached.value;
    }

    const inflight = generationJobsListInflight.get(cacheKey);
    if (inflight) {
      return inflight;
    }

    const scope = captureUserScopeSnapshot();
    const version = generationJobsListVersion;
    const request: Promise<ListJobsResponse> = api
      .get<ListJobsResponse>('/api/generation-jobs', { params })
      .then((response) => {
        assertUserScopeUnchanged(scope);
        // A create/cancel/delete invalidated this snapshot while it was in
        // flight. Rejoin the current request instead of restoring old jobs.
        if (version !== generationJobsListVersion) {
          return generationJobsApi.listJobs(params);
        }
        generationJobsListCache.set(cacheKey, {
          fetchedAt: Date.now(),
          value: response.data,
        });
        return response.data;
      })
      .finally(() => {
        if (generationJobsListInflight.get(cacheKey) === request) {
          generationJobsListInflight.delete(cacheKey);
        }
      });

    generationJobsListInflight.set(cacheKey, request);
    return request;
  },

  async getActiveWorkbookJob(workbookId: number): Promise<ActiveWorkbookGenerationJobResponse> {
    const response = await api.get<ActiveWorkbookGenerationJobResponse>(
      `/api/generation-jobs/active-workbook/${workbookId}`
    );
    return response.data;
  },

  /**
   * Reconcile a job against the workbook_generation_items ledger.
   *
   * Works even when the generation_jobs row is already deleted (completed jobs
   * are auto-removed): the backend derives state from remaining in-flight
   * workbook ledger slots, so the widget can recover the true complete /
   * partial-failure / still-running state after a polling loop stalls.
   */
  async reconcileJob(jobId: string, workbookId?: number | null): Promise<ReconcileJobResponse> {
    const params = workbookId != null ? `?workbook_id=${workbookId}` : '';
    const response = await api.get<ReconcileJobResponse>(
      `/api/generation-jobs/${jobId}/reconcile${params}`
    );
    return response.data;
  },

  async getActiveJobs(workbookId?: number): Promise<{ jobs: GenerationJobSummary[]; count: number }> {
    const params = workbookId !== undefined ? `?workbook_id=${workbookId}` : '';
    const response = await api.get<{ jobs: GenerationJobSummary[]; count: number }>(
      `/api/generation-jobs/active${params}`
    );
    return response.data;
  },

  /**
   * Delete a finished generation job
   *
   * Only completed, cancelled, or failed jobs can be deleted.
   * Active jobs must be cancelled first.
   */
  async deleteJob(jobId: string, action = 'job_cleanup'): Promise<{ message: string; job_id: string }> {
    const response = await api.delete(`/api/generation-jobs/${jobId}`, { headers: generationAuditHeaders(action) });
    clearGenerationJobsListCache();
    return response.data;
  },

  /** Only the winning page starts the paid follow-up, including across devices. */
  async claimFollowUp(jobId: string): Promise<{ job_id: string; claimed: boolean; action?: unknown }> {
    const response = await api.post(`/api/generation-jobs/${jobId}/claim-follow-up`, undefined, {
      ...NO_RETRY,
      headers: generationAuditHeaders('job_follow_up_claim'),
    });
    return response.data;
  },

  /**
   * Send error details to administrator via email
   *
   * @param request - Error report data
   * @returns Success message
   */
  async sendErrorEmail(request: {
    workbook_title: string;
    job_id: string;
    error_details: string;
  }): Promise<{ message: string; job_id: string }> {
    const response = await api.post(`/api/generation-jobs/${request.job_id}/send-error-email`, request);
    clearGenerationJobsListCache();
    return response.data;
  },
};

export interface ReconcileJobResponse {
  job_id: string;
  job_exists: boolean;
  job_status: string | null;
  workbook_id: number | null;
  has_active_items: boolean;
  active_count: number;
  error_count: number;
  errors: NonNullable<GenerationJob['errors']>;
  error_message: string | null;
  error_code: GenerationJob['error_code'];
  is_complete: boolean;
}

/**
 * Credit History API
 *
 * Fetch paginated credit usage history from WordPress.
 */
export const creditHistoryApi = {
  /**
   * Get credit usage history with pagination
   *
   * @param params - Pagination parameters (page, per_page)
   * @returns Paginated credit history log
   */
  async getHistory(params?: GetCreditHistoryParams): Promise<GetCreditHistoryResponse> {
    const response = await api.get<GetCreditHistoryResponse>('/api/user/credits/history', {
      params: params || { page: 1, per_page: 30 },
    });
    return response.data;
  },

  async exportHistory(params: GetCreditHistoryExportParams): Promise<void> {
    const response = await api.get('/api/user/credits/history/export', {
      params,
      responseType: 'blob',
    });
    const filename = getFilenameFromContentDisposition(
      response.headers['content-disposition'],
      'autoqm_credit_history.xlsx'
    );
    const contentType =
      response.headers['content-type'] ||
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const blob = new Blob([response.data], { type: contentType });
    downloadBlob(blob, filename);
  },
};

/**
 * Used Files API
 *
 * Fetch paginated used files (변형문제) log from WordPress.
 */
export const usedFilesApi = {
  /**
   * Get used files log with pagination
   *
   * @param params - Pagination parameters (page, per_page)
   * @returns Paginated used files log
   */
  async getUsedFiles(params?: GetUsedFilesParams): Promise<GetUsedFilesResponse> {
    const response = await api.get<GetUsedFilesResponse>('/api/user/used-files', {
      params: params || { page: 1, per_page: 30 },
    });
    return response.data;
  },
};

// Session Init API - consolidates credits, notifications, and public settings into one call
export interface SessionInitResponse {
  credits: { credits: number; user_id: number; success: boolean } | null;
  notification_counts: {
    unread_count: number;
    admin_badge_counts: { pending_refunds: number; unread_chats: number } | null;
  };
  public_settings: {
    passage_min_words: number;
    passage_max_words: number;
    oneclick_mocktest_menu_public?: boolean;
  };
}

// Editor Init API - consolidates editor preferences into one call
export interface EditorInitResponse {
  preferences: {
    font: FontSettings | null;
    margin: MarginSettings | null;
    doc_editing: EditingOptions | null;
  };
  question_options: {
    options: Record<string, unknown>;
    manualPlacements: Record<string, unknown>;
    updatedAt: string | null;
    version: number;
  };
}

/**
 * Session initialization API.
 * Fetches credits, notification counts, and public settings in a single request.
 * Used by LayoutBootstrap to seed context providers without multiple API calls.
 */
export async function fetchSessionInit(): Promise<SessionInitResponse> {
  const response = await api.get<SessionInitResponse>('/api/user/session-init');
  return response.data;
}

/**
 * Editor initialization API.
 * Fetches font, margin, document editing preferences, and question options in a single request.
 * Used by EditorProviders to seed editor context providers without multiple API calls.
 */
export async function fetchEditorInit(): Promise<EditorInitResponse> {
  const response = await api.get<EditorInitResponse>('/api/user/editor-init');
  return response.data;
}
